import axios from 'axios'
import store from '@/store'
import router from '@/router'
import i18n from '@/i18n'
import { logout } from '@/utils/user'

import { showNotice } from '@/utils/tool'

import { setupCache } from 'axios-cache-adapter'
import localforage from 'localforage'
const forageStore = localforage.createInstance({
  // List of drivers used
  driver: [
    localforage.LOCALSTORAGE
  ],
  // Prefix all storage keys to prevent conflicts
  name: 'my-cache'
})

// Create `axios-cache-adapter` instance
const cache = setupCache({
  store: forageStore
})

// ETag 快取：第一次收到資料時把 ETag 與資料存進 IndexedDB（localStorage 只有 5MB 不夠放），
// 之後帶 If-None-Match，後端回 304 就改用 IndexedDB 的資料
// 呼叫端以 customConfig: { etagCache: true } 開啟
// etag 與 data 分開存，送 request 時只讀小的 etag，304 時才讀大的 data
const etagStore = localforage.createInstance({
  driver: [
    localforage.INDEXEDDB
  ],
  name: 'etag-cache'
})
const ETAG_CACHE_LIMIT = 2 // 最多保留幾筆（例如切換語言），超過就淘汰最舊的
const ETAG_RECENT_KEY = '__recent__'

function getEtagCacheKey(config, formData) {
  const params = []
  for (const [key, value] of formData.entries()) {
    if (key !== 'token') params.push(`${key}=${value}`)
  }
  params.sort()
  return (config.baseURL || '') + config.url + '?' + params.join('&')
}

async function readEtag(key) {
  try {
    return await etagStore.getItem(key + '|etag')
  } catch (e) {
    // 不支援 IndexedDB（如部分無痕模式）就當作沒有快取
    return null
  }
}

async function readEtagData(key) {
  try {
    return await etagStore.getItem(key + '|data')
  } catch (e) {
    return null
  }
}

async function writeEtagCache(key, etag, data) {
  try {
    // 先寫 data 再寫 etag，確保有 etag 就一定有 data
    await etagStore.setItem(key + '|data', data)
    await etagStore.setItem(key + '|etag', etag)

    const recent = [key, ...((await etagStore.getItem(ETAG_RECENT_KEY)) || []).filter(k => k !== key)]
      .slice(0, ETAG_CACHE_LIMIT)
    await etagStore.setItem(ETAG_RECENT_KEY, recent)

    // 清掉不在保留名單的資料（含被淘汰的與舊格式留下的）
    const keep = new Set([ETAG_RECENT_KEY])
    recent.forEach(k => keep.add(k + '|etag').add(k + '|data'))
    const keys = await etagStore.keys()
    for (const k of keys) {
      if (!keep.has(k)) await etagStore.removeItem(k)
    }
  } catch (e) {
    // 寫入失敗：清掉這筆，下次就不會帶 If-None-Match
    etagStore.removeItem(key + '|etag').catch(() => {})
  }
}

// create an axios instance
const service = axios.create({
  adapter: cache.adapter,
  timeout: 60000 // request timeout
})
// request interceptor
service.interceptors.request.use(
  async config => {
    if (!(config.customConfig && config.customConfig.noLoading)) {
      store.commit('SET_LOADING_COUNT_PLUS')
    }

    const formData = new FormData()
    for (var key in config['data']) {
      if (config['data'][key] !== null && config['data'][key] !== undefined) {
        formData.set(key, config['data'][key])
      }
    }

    if (!formData.has('code')) {
      let brandCode = store.getters.brandCode
      if (!brandCode || brandCode === '') {
        brandCode = process.env.VUE_APP_BRAND_CODE // 開發環境指定brand code
        if (brandCode === '') {
          brandCode = window.location.host || document.location.host // 剛開始載入先用host取資訊
        }
      }

      formData.set('code', brandCode)
    }

    if (!formData.has('token') && store.getters.token) {
      formData.set('token', store.getters.token)
    }

    if (!formData.has('language')) {
      formData.set('language', store.getters.language)
    }

    config['data'] = formData

    if (config.customConfig && config.customConfig.etagCache) {
      const cacheKey = getEtagCacheKey(config, formData)
      const etag = await readEtag(cacheKey)
      config.etagCacheKey = cacheKey
      if (etag) {
        config.headers['If-None-Match'] = etag
      }
      config.validateStatus = status => (status >= 200 && status < 300) || status === 304
    }

    // store.commit('SET_LOADING', true)
    return config
  },
)

// response interceptor
service.interceptors.response.use(
  async function(response) {
    if (!(response.config.customConfig && response.config.customConfig.noLoading)) {
      store.commit('SET_LOADING_COUNT_MINUS')
    }

    const cacheKey = response.config.etagCacheKey
    if (cacheKey) {
      if (response.status === 304) {
        // 版本沒變，用本地資料
        const data = await readEtagData(cacheKey)
        response.data = data || response.data
      } else {
        const etag = response.headers['etag']
        if (etag) writeEtagCache(cacheKey, etag, response.data)
      }
    }

    return response
  },
  error => {
    if (!(error.config.customConfig && error.config.customConfig.noLoading)) {
      store.commit('SET_LOADING_COUNT_MINUS')
    }
    store.commit('SET_LOADING', false)
    const showErrorMsg = !(error.config && error.config.customConfig && error.config.customConfig.mute)

    if (error.response === undefined) {
      // 其他錯誤訊息
      const msg = i18n.te('Bao.please_check_network') ? i18n.t('Bao.please_check_network') : 'Please check your network status.'
      if (showErrorMsg) {
        showNotice(msg, function() {
          if (router.currentRoute.name === 'error' || router.currentRoute.name === 'critical') {
            window.top.location.href = '/'
          } else {
            window.top.location.reload()
          }
        })
      }
    } else {
      const content = error.response.data
      // console.clear()
      if (content.status === 401) {
        // 重新登入
        logout(content.msg)
      } else if (content.status === 403) {
        // 權限不足 回首頁
        // showNotice(content.msg, function() {
        //   router.push({ path: '/index' })
        // })
      } else if (content.status === 510) {
        // toErrorPage(content.data.content)
      } else if (content.status === 406) {
        // 表單欄位顯示 不由這邊處理
      } else if (content.status === 505) {
        // 不由這邊處理
      } else {
        // 其他錯誤訊息
        if (showErrorMsg) {
          showNotice(content.msg)
        }
      }
    }

    return Promise.reject(error)
  }
)

export default service
